import type { SiteStatusSummary } from '../../domain/siteNote';
import type { SitePenCounts } from '../../domain/sitePenCounts';
import type { CustomerSiteInsight } from '../customers/customer-insights';

/** A country's or region's sites added up, for the status table's drilldown rows. */
export interface SiteStatusTotals {
  sites: number;
  /** Sum over sites with ordered systems set; undefined when none has it. */
  ordered?: number | undefined;
  /** Complete systems on site: see SiteCompleteness.assembled. */
  systems: number;
  active: number;
  /** Undefined when no site has farm data. */
  pensWithFish?: number | undefined;
  pensWithData?: number | undefined;
  /** Sites with missing or extra parts. */
  sitesWithGaps: number;
  openRequests: number;
  /** The newest last contact among the sites. */
  lastContact?: string | undefined;
}

export function siteStatusTotals(
  sites: readonly CustomerSiteInsight[],
  pensBySite: { get(siteId: string): SitePenCounts | undefined },
  openRequestsBySite: ReadonlyMap<string, number>,
  statusBySite: ReadonlyMap<string, SiteStatusSummary>,
): SiteStatusTotals {
  const totals: SiteStatusTotals = {
    sites: sites.length,
    systems: 0,
    active: 0,
    sitesWithGaps: 0,
    openRequests: 0,
  };
  for (const { site, completeness } of sites) {
    if (site.orderedSystems !== undefined) {
      totals.ordered = (totals.ordered ?? 0) + site.orderedSystems;
    }
    totals.systems += completeness.assembled;
    totals.active += completeness.active;
    const pens = pensBySite.get(site.id);
    if (pens) {
      totals.pensWithFish = (totals.pensWithFish ?? 0) + pens.pensWithFish;
      totals.pensWithData = (totals.pensWithData ?? 0) + pens.pensWithData;
    }
    if (completeness.gaps.length > 0) totals.sitesWithGaps += 1;
    totals.openRequests += openRequestsBySite.get(site.id) ?? 0;
    const contact = statusBySite.get(site.id)?.lastContact?.createTime;
    if (contact && (!totals.lastContact || contact > totals.lastContact)) {
      totals.lastContact = contact;
    }
  }
  return totals;
}
